import { useMemo, useReducer, Dispatch, useEffect } from 'react'
import useQuery, { UseQueryState } from 'shared/hooks/useQuery'
import { useDispatch } from 'react-redux'
import useMutation, { MutationFunctionType } from 'shared/hooks/useMutation'
import { showDialog } from 'shared/redux/app/reducer'
import tableReducer, { tableInitialState, TableState, TableAction } from 'shared/components/DataTable/tableReducer'
import useSearch from './useSearch'
import capitalize from 'lodash/capitalize'
import { PaginatedList, ColumnSort, Identifiable } from 'shared/types'
import qs from 'qs'
import ConfirmDialog from 'shared/components/ConfirmDialog'

export type UsePageTableHandlers = {
  onCreate: MutationFunctionType
  onDelete: MutationFunctionType
  onUpdate: MutationFunctionType
  onSearch: (search: string) => void
  // onConfirmDeleteMultiple: (ids: string[]) => void
  onConfirmDelete: (row: NamedEntity) => void
  tableDispatch: Dispatch<TableAction<Identifiable>>
  queryHandlers: {
    refetch(): void
  }
}
export interface NamedEntity extends Identifiable {
  name: string
}

interface UsePageTableProps {
  skip?: boolean
  isPaginated: boolean
  queryNode?: string
  mutationNode?: string
  initialSort?: ColumnSort[]
  queryUrl?: string
  isBaseQuery?: boolean
  isBaseCreate?: boolean
  isBaseUpdate?: boolean
  isBaseDelete?: boolean
  entityName?: string
  deleteMessage?: string
  queryParams?: {
    [key in string]: any
  }
  searchableFields?: string[]
}

const defaultSort: ColumnSort[] = [{ column: 'created_date', direction: 'desc' }]

function usePageTable<T extends Identifiable>(
  props: Omit<UsePageTableProps, 'isPaginated'> & { isPaginated: false },
): [{ tableState: TableState; rowResponse: UseQueryState<T[]> }, UsePageTableHandlers]

function usePageTable<T extends Identifiable>(
  props: Omit<UsePageTableProps, 'isPaginated'> & { isPaginated: true },
): [{ tableState: TableState; rowResponse: UseQueryState<PaginatedList<T>> }, UsePageTableHandlers]

function usePageTable<T extends Identifiable>(props: UsePageTableProps) {
  const {
    queryNode: node = '',
    mutationNode = node,
    queryParams = {},
    initialSort = defaultSort,
    queryUrl,
    isPaginated,
    searchableFields = [],
    isBaseQuery = true,
    isBaseCreate = true,
    isBaseUpdate = true,
    isBaseDelete = true,
    skip = false,
    deleteMessage = '',
    entityName = capitalize(node || mutationNode),
  } = props
  const listInitialData: T[] = []
  const paginatedListInitialData: PaginatedList<T> = { data: [], count: 0, additional_info: [] }
  const initialData: PaginatedList<T> | T[] = isPaginated ? paginatedListInitialData : listInitialData
  const [search, onSearch] = useSearch('')
  useEffect(syncSearch, [search])
  const [tableState, tableDispatch] = useReducer(tableReducer, { ...tableInitialState, sort: initialSort })
  const { sort, page, size } = tableState
  const queryString = qs.stringify({
    search: {
      fields: searchableFields,
      value: tableState.search,
    },
    sort,
    ...(isPaginated && { page, size }),
    ...queryParams,
  })

  const [rowResponse, queryHandlers] = useQuery(
    {
      url: `${queryUrl ? `${queryUrl}?${queryString}` : `/${node}?${queryString}`}`,
    },
    { initialData, isBase: isBaseQuery, skip },
  )
  const [, onCreate] = useMutation({
    url: `/${mutationNode}`,
    method: 'POST',
    isBase: isBaseCreate,
    onSuccess: queryHandlers.refetch,
    message: `${entityName} successfully created`,
  })
  const [, onUpdate] = useMutation({
    url: `/${mutationNode}`,
    method: 'PUT',
    isBase: isBaseUpdate,
    message: `${entityName} successfully updated`,
    onSuccess: queryHandlers.refetch,
  })
  const [, onDelete] = useMutation({
    url: `/${mutationNode}`,
    method: 'DELETE',
    isBase: isBaseDelete,
    onSuccess: () => {
      tableDispatch({ type: 'ResetSelected' })
      queryHandlers.refetch()
    },
    message: deleteMessage ? deleteMessage : `${entityName} successfully deleted`,
  })
  const dispatch = useDispatch()
  const states = useMemo(
    () => ({
      tableState,
      rowResponse,
    }),
    [tableState, rowResponse],
  )
  const handlers: UsePageTableHandlers = {
    onCreate,
    onDelete,
    onUpdate,
    onSearch,
    // onConfirmDeleteMultiple,
    onConfirmDelete,
    tableDispatch,
    queryHandlers,
  }
  return [states, handlers]

  function syncSearch() {
    tableDispatch({ type: 'SetSearch', payload: search })
  }

  function onConfirmDelete(row: NamedEntity) {
    dispatch(
      showDialog({
        component: ConfirmDialog,
        props: {
          title: `Are you sure you want to delete ${row.name}?`,
          onValid: () =>
            onDelete({
              data: { id: row.id },
              onSuccess: () => {
                queryHandlers.refetch()
              },
            }),
        },
      }),
    )
  }
}
export default usePageTable
